import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  USERS_PAGE_SIZE,
  createUser,
  deleteUser,
  getUserRoles,
  getUsers,
  updateUser,
} from '../services/user_service'
import './Users.css'

const emptyForm = {
  user_fullname: '',
  user_email: '',
  user_contact: '',
  role: '',
  user_status: 'active',
  password: '',
  reset_on_login: true,
}

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getUserId = (user) => getValue(user, ['userid', 'user_id', 'id'], '')
const getUserName = (user) =>
  getValue(user, ['user_fullname', 'fullName', 'fullname', 'name'])
const getInitials = (name) =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

function UserModal({ mode, user, roles, createdBy, onClose, onSaved }) {
  const isEdit = mode === 'edit'
  const [form, setForm] = useState(
    isEdit
      ? {
          user_fullname: getUserName(user),
          user_email: getValue(user, ['user_email', 'email'], ''),
          user_contact: getValue(
            user,
            ['user_contact', 'contact', 'phone'],
            '',
          ),
          role: String(getValue(user, ['role', 'roleid', 'role_id'], '')),
          user_status: getValue(user, ['user_status', 'status'], 'active'),
          password: '',
          reset_on_login: Boolean(
            getValue(user, ['reset_on_login', 'resetOnLogin'], false),
          ),
        }
      : emptyForm,
  )
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const updateField = (event) => {
    const { name, type, checked, value } = event.target
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }
  const submit = async (event) => {
    event.preventDefault()
    setIsSaving(true)
    setError('')
    try {
      const payload = {
        ...form,
        role: form.role ? Number(form.role) : null,
        user_created_by: createdBy || undefined,
      }
      if (!payload.password) delete payload.password
      if (isEdit) await updateUser(getUserId(user), payload)
      else await createUser(payload)
      onSaved()
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div
      className='users-modal__backdrop'
      role='presentation'
      onMouseDown={(event) =>
        event.target === event.currentTarget && !isSaving && onClose()
      }
    >
      <section
        className='users-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='user-form-title'
      >
        <div className='users-modal__header'>
          <div>
            <span className='app-content__eyebrow'>System users</span>
            <h2 id='user-form-title'>{isEdit ? 'Edit user' : 'Add user'}</h2>
          </div>
          <button
            type='button'
            onClick={onClose}
            disabled={isSaving}
            aria-label='Close user form'
          >
            x
          </button>
        </div>
        {error && (
          <div className='users-page__error' role='alert'>
            {error}
          </div>
        )}
        <form className='users-form' onSubmit={submit}>
          <label className='users-form__full'>
            Full name
            <input
              name='user_fullname'
              value={form.user_fullname}
              onChange={updateField}
              required
            />
          </label>
          <label>
            Email
            <input
              name='user_email'
              type='email'
              value={form.user_email}
              onChange={updateField}
              required
            />
          </label>
          <label>
            Contact
            <input
              name='user_contact'
              value={form.user_contact}
              onChange={updateField}
              required
            />
          </label>
          <label>
            Role
            <select
              name='role'
              value={form.role}
              onChange={updateField}
              required
            >
              <option value=''>Select a role</option>
              {roles.map((role) => (
                <option key={role.roleid} value={role.roleid}>
                  {role.role_name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              name='user_status'
              value={form.user_status}
              onChange={updateField}
            >
              <option value='active'>Active</option>
              <option value='inactive'>Inactive</option>
            </select>
          </label>
          <label className='users-form__full'>
            {isEdit ? 'New password' : 'Password'}
            <input
              name='password'
              type='password'
              minLength='6'
              value={form.password}
              onChange={updateField}
              required={!isEdit}
              placeholder={
                isEdit ? 'Leave blank to keep the current password' : ''
              }
            />
          </label>
          <label className='users-form__check'>
            <input
              name='reset_on_login'
              type='checkbox'
              checked={form.reset_on_login}
              onChange={updateField}
            />
            Require password reset on next login
          </label>
          <div className='users-modal__actions'>
            <button type='button' onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              className='users-button--primary'
              type='submit'
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : isEdit ? 'Save changes' : 'Add user'}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function Users() {
  const { user: authUser, hasPermission } = useAuth()
  const canEditUser = hasPermission('editUser')
  const createdBy = authUser?.userid ?? authUser?.user_id ?? authUser?.id
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [nextPage, setNextPage] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [modal, setModal] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [error, setError] = useState('')

  const loadUsers = async (page = 1, query = search, signal) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getUsers({ page, search: query, signal })
      if (signal?.aborted) return
      setUsers(result.users)
      setCurrentPage(result.currentPage)
      setNextPage(result.nextPage)
    } catch (requestError) {
      if (requestError.name !== 'AbortError') setError(requestError.message)
    } finally {
      if (!signal?.aborted) setIsLoading(false)
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    getUserRoles(controller.signal)
      .then(setRoles)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    const timer = window.setTimeout(
      () => loadUsers(1, search, controller.signal),
      search ? 350 : 0,
    )
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const refresh = async () => {
    setModal(null)
    await loadUsers(currentPage)
  }
  const confirmDelete = async () => {
    setIsDeleting(true)
    setError('')
    try {
      await deleteUser(getUserId(pendingDelete))
      setPendingDelete(null)
      await loadUsers(currentPage)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className='users-page'>
      <nav className='users-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Users</strong>
      </nav>
      <header className='users-page__header'>
        <div>
          <span className='app-content__eyebrow'>System users</span>
          <h1>Users</h1>
          <p>Manage staff accounts, roles, and access status.</p>
        </div>
        <div className='users-page__commands'>
          {canEditUser && (
            <>
              <Link to='/admin/users/permissions'>Permissions</Link>
              <button type='button' onClick={() => setModal({ mode: 'add' })}>
                + Add user
              </button>
            </>
          )}
        </div>
      </header>
      {error && (
        <div className='users-page__error' role='alert'>
          {error}
        </div>
      )}
      <div className='users-toolbar'>
        <label>
          <span aria-hidden='true'>search</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder='Search by name, email, or contact'
            aria-label='Search users'
          />
        </label>
        <span>{users.length} users on this page</span>
      </div>
      {isLoading ? (
        <div className='users-empty'>Loading users...</div>
      ) : users.length ? (
        <section className='users-grid' aria-label='System users'>
          {users.map((user, index) => {
            const name = getUserName(user)
            return (
              <article
                className='user-card'
                key={`${getUserId(user)}-${index}`}
              >
                <div className='user-card__profile'>
                  <div className='user-card__avatar' aria-hidden='true'>
                    {getInitials(name)}
                  </div>
                  <h2>{name}</h2>
                  <span>
                    {getValue(user, ['user_role', 'role_name'], 'Unassigned')}
                  </span>
                </div>
                <dl className='user-card__details'>
                  <div>
                    <dt>Email</dt>
                    <dd>{getValue(user, ['user_email', 'email'])}</dd>
                  </div>
                  <div>
                    <dt>Phone</dt>
                    <dd>
                      {getValue(user, ['user_contact', 'contact', 'phone'])}
                    </dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <span
                        className={`user-status user-status--${getValue(user, ['user_status', 'status'], 'active').toLowerCase()}`}
                      >
                        {getValue(user, ['user_status', 'status'], 'active')}
                      </span>
                    </dd>
                  </div>
                </dl>
                <div className='user-card__actions'>
                  {canEditUser && (
                    <button
                      type='button'
                      onClick={() => setModal({ mode: 'edit', user })}
                    >
                      Edit
                    </button>
                  )}
                  {canEditUser && (
                    <button
                      className='user-card__delete'
                      type='button'
                      onClick={() => setPendingDelete(user)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </article>
            )
          })}
        </section>
      ) : (
        <div className='users-empty'>No users match your search.</div>
      )}
      <div className='users-pagination'>
        <span>Showing up to {USERS_PAGE_SIZE} users</span>
        <div>
          <button
            type='button'
            onClick={() => loadUsers(currentPage - 1)}
            disabled={currentPage <= 1 || isLoading}
          >
            Previous
          </button>
          <button
            type='button'
            onClick={() => loadUsers(nextPage)}
            disabled={!nextPage || isLoading}
          >
            Next
          </button>
        </div>
      </div>
      {modal && (
        <UserModal
          mode={modal.mode}
          user={modal.user}
          roles={roles}
          createdBy={createdBy}
          onClose={() => setModal(null)}
          onSaved={refresh}
        />
      )}
      {pendingDelete && (
        <div
          className='users-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isDeleting &&
            setPendingDelete(null)
          }
        >
          <section
            className='users-modal users-modal--confirm'
            role='alertdialog'
            aria-modal='true'
            aria-labelledby='delete-user-title'
          >
            <div className='users-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Confirm action</span>
                <h2 id='delete-user-title'>Delete user?</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingDelete(null)}
                disabled={isDeleting}
                aria-label='Close confirmation'
              >
                x
              </button>
            </div>
            <p>
              <strong>{getUserName(pendingDelete)}</strong> will permanently
              lose access to the system.
            </p>
            <div className='users-modal__actions'>
              <button
                type='button'
                onClick={() => setPendingDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className='users-button--danger'
                type='button'
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete user'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default Users
